/*
 * V1 → V2 color token mapping data and types.
 */

import type { primitiveColorTokens, semanticColorTokensLight } from '../generated/base-color-tokens';
import type { PrimitiveColorTokensV2Type, SemanticColorTokensV2CssVarsType } from '../generated/base-color-tokens-v2';

/** All Base 1.0 / 2.0 color token keys from the generated specs. */
export type V1PrimitiveTokenKey = keyof typeof primitiveColorTokens;
export type V2PrimitiveTokenKey = keyof PrimitiveColorTokensV2Type;
export type V1SemanticTokenKey = keyof ReturnType<typeof semanticColorTokensLight>;
export type V2SemanticTokenKey = keyof SemanticColorTokensV2CssVarsType;
export const V1_PRIMITIVE_TO_V2_PRIMITIVE = (({
  amber100: 'amber96',
  amber100Dark: 'amber27',
  amber200: 'amber91',
  amber200Dark: 'amber35',
  amber300: 'amber83',
  amber300Dark: 'amber40',
  amber400: 'amber74',
  amber400Dark: 'amber48',
  amber50: 'amber98',
  amber500: 'amber61',
  amber500Dark: 'amber57',
  amber50Dark: 'amber18',
  amber600: 'amber57',
  amber600Dark: 'amber66',
  amber700: 'amber48',
  amber700Dark: 'amber79',
  amber800: 'amber44',
  amber800Dark: 'amber83',
  amber900: 'amber27',
  amber900Dark: 'amber91',
  black: 'neutral0',
  blue100: 'blue96',
  blue100Dark: 'blue27',
  blue200: 'blue91',
  blue200Dark: 'blue35',
  blue300: 'blue83',
  blue300Dark: 'blue40',
  blue400: 'blue74',
  blue400Dark: 'blue48',
  blue50: 'blue98',
  blue500: 'blue61',
  blue500Dark: 'blue57',
  blue50Dark: 'blue18',
  blue600: 'blue57',
  blue600Dark: 'blue66',
  blue700: 'blue48',
  blue700Dark: 'blue79',
  blue800: 'blue44',
  blue800Dark: 'blue83',
  blue900: 'blue27',
  blue900Dark: 'blue91',
  brandAi0: 'indigo0',
  brandAi100: 'indigo100',
  brandAi15: 'indigo15',
  brandAi18: 'indigo18',
  brandAi23: 'indigo23',
  brandAi27: 'indigo27',
  brandAi31: 'indigo31',
  brandAi35: 'indigo35',
  brandAi40: 'indigo40',
  brandAi44: 'indigo44',
  brandAi48: 'indigo48',
  brandAi53: 'indigo53',
  brandAi57: 'indigo57',
  brandAi61: 'indigo61',
  brandAi66: 'indigo66',
  brandAi70: 'indigo70',
  brandAi74: 'indigo74',
  brandAi79: 'indigo79',
  brandAi83: 'indigo83',
  brandAi87: 'indigo87',
  brandAi91: 'indigo91',
  brandAi96: 'indigo96',
  brandAi98: 'indigo98',
  brandAi99: 'indigo99',
  brandAiGradient0: 'gradientIndigo0',
  brandAiGradient100: 'gradientIndigo100',
  brandAiGradient15: 'gradientIndigo15',
  brandAiGradient18: 'gradientIndigo18',
  brandAiGradient23: 'gradientIndigo23',
  brandAiGradient27: 'gradientIndigo27',
  brandAiGradient31: 'gradientIndigo31',
  brandAiGradient35: 'gradientIndigo35',
  brandAiGradient40: 'gradientIndigo40',
  brandAiGradient44: 'gradientIndigo44',
  brandAiGradient48: 'gradientIndigo48',
  brandAiGradient53: 'gradientIndigo53',
  brandAiGradient57: 'gradientIndigo57',
  brandAiGradient61: 'gradientIndigo61',
  brandAiGradient66: 'gradientIndigo66',
  brandAiGradient70: 'gradientIndigo70',
  brandAiGradient74: 'gradientIndigo74',
  brandAiGradient79: 'gradientIndigo79',
  brandAiGradient83: 'gradientIndigo83',
  brandAiGradient87: 'gradientIndigo87',
  brandAiGradient91: 'gradientIndigo91',
  brandAiGradient96: 'gradientIndigo96',
  brandAiGradient98: 'gradientIndigo98',
  brandAiGradient99: 'gradientIndigo99',
  brandDefault100: 'blue96',
  brandDefault100Dark: 'blue27',
  brandDefault200: 'blue91',
  brandDefault200Dark: 'blue35',
  brandDefault300: 'blue83',
  brandDefault300Dark: 'blue40',
  brandDefault400: 'blue74',
  brandDefault400Dark: 'blue48',
  brandDefault50: 'blue98',
  brandDefault500: 'blue61',
  brandDefault500Dark: 'blue57',
  brandDefault50Dark: 'blue18',
  brandDefault600: 'blue57',
  brandDefault600Dark: 'blue66',
  brandDefault700: 'blue48',
  brandDefault700Dark: 'blue79',
  brandDefault800: 'blue44',
  brandDefault800Dark: 'blue83',
  brandDefault900: 'blue27',
  brandDefault900Dark: 'blue91',
  brandDefaultGradient0: 'gradientBlue0',
  brandDefaultGradient100: 'gradientBlue100',
  brandDefaultGradient15: 'gradientBlue0',
  brandDefaultGradient20: 'gradientBlue0',
  brandDefaultGradient25: 'gradientBlue0',
  brandDefaultGradient30: 'gradientBlue18',
  brandDefaultGradient35: 'gradientBlue23',
  brandDefaultGradient40: 'gradientBlue31',
  brandDefaultGradient45: 'gradientBlue44',
  brandDefaultGradient50: 'gradientBlue48',
  brandDefaultGradient57: 'gradientBlue57',
  brandDefaultGradient66: 'gradientBlue66',
  brandDefaultGradient74: 'gradientBlue74',
  brandDefaultGradient82: 'gradientBlue79',
  brandDefaultGradient90: 'gradientBlue87',
  brandDefaultGradient94: 'gradientBlue96',
  brandDefaultGradient97: 'gradientBlue99',
  brandDefaultGradient99: 'gradientBlue100',
  brandEats100: 'green96',
  brandEats100Dark: 'green27',
  brandEats200: 'green91',
  brandEats200Dark: 'green35',
  brandEats300: 'green83',
  brandEats300Dark: 'green40',
  brandEats400: 'green70',
  brandEats400Dark: 'green48',
  brandEats50: 'green98',
  brandEats500: 'green61',
  brandEats500Dark: 'green53',
  brandEats50Dark: 'green18',
  brandEats600: 'green53',
  brandEats600Dark: 'green61',
  brandEats700: 'green48',
  brandEats700Dark: 'green74',
  brandEats800: 'green40',
  brandEats800Dark: 'green83',
  brandEats900: 'green27',
  brandEats900Dark: 'green91',
  brandOffers0: 'red0',
  brandOffers100: 'red100',
  brandOffers15: 'red15',
  brandOffers18: 'red18',
  brandOffers23: 'red23',
  brandOffers27: 'red27',
  brandOffers31: 'red31',
  brandOffers35: 'red35',
  brandOffers40: 'red40',
  brandOffers44: 'red44',
  brandOffers48: 'red48',
  brandOffers53: 'red48',
  brandOffers57: 'red53',
  brandOffers61: 'red57',
  brandOffers66: 'red61',
  brandOffers70: 'red66',
  brandOffers74: 'red74',
  brandOffers79: 'red79',
  brandOffers83: 'red83',
  brandOffers87: 'red87',
  brandOffers91: 'red91',
  brandOffers96: 'red96',
  brandOffers98: 'red98',
  brandOffers99: 'red99',
  brandOffersGradient0: 'gradientRed0',
  brandOffersGradient100: 'gradientRed100',
  brandOffersGradient15: 'gradientRed15',
  brandOffersGradient18: 'gradientRed18',
  brandOffersGradient23: 'gradientRed23',
  brandOffersGradient27: 'gradientRed27',
  brandOffersGradient31: 'gradientRed31',
  brandOffersGradient35: 'gradientRed35',
  brandOffersGradient40: 'gradientRed40',
  brandOffersGradient44: 'gradientRed44',
  brandOffersGradient48: 'gradientRed48',
  brandOffersGradient53: 'gradientRed53',
  brandOffersGradient57: 'gradientRed57',
  brandOffersGradient61: 'gradientRed61',
  brandOffersGradient66: 'gradientRed66',
  brandOffersGradient70: 'gradientRed70',
  brandOffersGradient74: 'gradientRed74',
  brandOffersGradient79: 'gradientRed79',
  brandOffersGradient83: 'gradientRed83',
  brandOffersGradient87: 'gradientRed87',
  brandOffersGradient91: 'gradientRed91',
  brandOffersGradient96: 'gradientRed96',
  brandOffersGradient98: 'gradientRed98',
  brandOffersGradient99: 'gradientRed99',
  gray100: 'neutral96',
  gray100Dark: 'neutral27',
  gray200: 'neutral91',
  gray200Dark: 'neutral35',
  gray300: 'neutral79',
  gray300Dark: 'neutral40',
  gray400: 'neutral74',
  gray400Dark: 'neutral48',
  gray50: 'neutral98',
  gray500: 'neutral61',
  gray500Dark: 'neutral57',
  gray50Dark: 'neutral18',
  gray600: 'neutral57',
  gray600Dark: 'neutral66',
  gray700: 'neutral48',
  gray700Dark: 'neutral74',
  gray800: 'neutral40',
  gray800Dark: 'neutral83',
  gray900: 'neutral27',
  gray900Dark: 'neutral91',
  green100: 'green96',
  green100Dark: 'green27',
  green200: 'green91',
  green200Dark: 'green35',
  green300: 'green83',
  green300Dark: 'green40',
  green400: 'green70',
  green400Dark: 'green48',
  green50: 'green98',
  green500: 'green61',
  green500Dark: 'green53',
  green50Dark: 'green18',
  green600: 'green53',
  green600Dark: 'green61',
  green700: 'green48',
  green700Dark: 'green74',
  green800: 'green40',
  green800Dark: 'green83',
  green900: 'green27',
  green900Dark: 'green91',
  lime100: 'lime91',
  lime100Dark: 'lime27',
  lime200: 'lime87',
  lime200Dark: 'lime35',
  lime300: 'lime83',
  lime300Dark: 'lime40',
  lime400: 'lime70',
  lime400Dark: 'lime48',
  lime50: 'lime98',
  lime500: 'lime61',
  lime500Dark: 'lime57',
  lime50Dark: 'lime18',
  lime600: 'lime53',
  lime600Dark: 'lime66',
  lime700: 'lime48',
  lime700Dark: 'lime74',
  lime800: 'lime40',
  lime800Dark: 'lime83',
  lime900: 'lime27',
  lime900Dark: 'lime91',
  magenta100: 'magenta96',
  magenta100Dark: 'magenta27',
  magenta200: 'magenta91',
  magenta200Dark: 'magenta35',
  magenta300: 'magenta87',
  magenta300Dark: 'magenta40',
  magenta400: 'magenta74',
  magenta400Dark: 'magenta48',
  magenta50: 'magenta98',
  magenta500: 'magenta66',
  magenta500Dark: 'magenta57',
  magenta50Dark: 'magenta18',
  magenta600: 'magenta57',
  magenta600Dark: 'magenta66',
  magenta700: 'magenta53',
  magenta700Dark: 'magenta79',
  magenta800: 'magenta44',
  magenta800Dark: 'magenta83',
  magenta900: 'magenta31',
  magenta900Dark: 'magenta91',
  orange100: 'orange96',
  orange100Dark: 'orange27',
  orange200: 'orange91',
  orange200Dark: 'orange35',
  orange300: 'orange83',
  orange300Dark: 'orange40',
  orange400: 'orange74',
  orange400Dark: 'orange48',
  orange50: 'orange98',
  orange500: 'orange66',
  orange500Dark: 'orange57',
  orange50Dark: 'orange18',
  orange600: 'orange57',
  orange600Dark: 'orange66',
  orange700: 'orange48',
  orange700Dark: 'orange74',
  orange800: 'orange44',
  orange800Dark: 'orange83',
  orange900: 'orange27',
  orange900Dark: 'orange91',
  periwinkle100: 'indigo96',
  periwinkle100Dark: 'indigo27',
  periwinkle200: 'indigo91',
  periwinkle200Dark: 'indigo35',
  periwinkle300: 'indigo83',
  periwinkle300Dark: 'indigo40',
  periwinkle400: 'indigo74',
  periwinkle400Dark: 'indigo48',
  periwinkle50: 'indigo98',
  periwinkle500: 'indigo61',
  periwinkle500Dark: 'indigo57',
  periwinkle50Dark: 'indigo18',
  periwinkle600: 'indigo57',
  periwinkle600Dark: 'indigo66',
  periwinkle700: 'indigo48',
  periwinkle700Dark: 'indigo79',
  periwinkle800: 'indigo40',
  periwinkle800Dark: 'indigo87',
  periwinkle900: 'indigo27',
  periwinkle900Dark: 'indigo91',
  purple100: 'purple96',
  purple100Dark: 'purple27',
  purple200: 'purple91',
  purple200Dark: 'purple35',
  purple300: 'purple83',
  purple300Dark: 'purple40',
  purple400: 'purple74',
  purple400Dark: 'purple48',
  purple50: 'purple98',
  purple500: 'purple66',
  purple500Dark: 'purple57',
  purple50Dark: 'purple18',
  purple600: 'purple57',
  purple600Dark: 'purple66',
  purple700: 'purple53',
  purple700Dark: 'purple79',
  purple800: 'purple44',
  purple800Dark: 'purple83',
  purple900: 'purple31',
  purple900Dark: 'purple91',
  red100: 'red96',
  red100Dark: 'red27',
  red200: 'red91',
  red200Dark: 'red35',
  red300: 'red83',
  red300Dark: 'red40',
  red400: 'red74',
  red400Dark: 'red48',
  red50: 'red98',
  red500: 'red61',
  red500Dark: 'red53',
  red50Dark: 'red18',
  red600: 'red57',
  red600Dark: 'red61',
  red700: 'red48',
  red700Dark: 'red79',
  red800: 'red44',
  red800Dark: 'red83',
  red900: 'red27',
  red900Dark: 'red91',
  teal100: 'teal96',
  teal100Dark: 'teal27',
  teal200: 'teal91',
  teal200Dark: 'teal35',
  teal300: 'teal83',
  teal300Dark: 'teal40',
  teal400: 'teal74',
  teal400Dark: 'teal48',
  teal50: 'teal98',
  teal500: 'teal61',
  teal500Dark: 'teal57',
  teal50Dark: 'teal18',
  teal600: 'teal53',
  teal600Dark: 'teal66',
  teal700: 'teal48',
  teal700Dark: 'teal79',
  teal800: 'teal40',
  teal800Dark: 'teal83',
  teal900: 'teal27',
  teal900Dark: 'teal91',
  white: 'neutral100',
  yellow100: 'yellow96',
  yellow100Dark: 'yellow27',
  yellow200: 'yellow91',
  yellow200Dark: 'yellow35',
  yellow300: 'yellow83',
  yellow300Dark: 'yellow40',
  yellow400: 'yellow74',
  yellow400Dark: 'yellow48',
  yellow50: 'yellow98',
  yellow500: 'yellow61',
  yellow500Dark: 'yellow57',
  yellow50Dark: 'yellow18',
  yellow600: 'yellow57',
  yellow600Dark: 'yellow66',
  yellow700: 'yellow48',
  yellow700Dark: 'yellow79',
  yellow800: 'yellow44',
  yellow800Dark: 'yellow83',
  yellow900: 'yellow27',
  yellow900Dark: 'yellow91'
} as const) satisfies Partial<Record<V1PrimitiveTokenKey, V2PrimitiveTokenKey>>);
export const V1_SEMANTIC_TO_V2_SEMANTIC = (({
  backgroundAlwaysDark: 'backgroundAlwaysDark',
  backgroundAlwaysLight: 'backgroundAlwaysLight',
  backgroundInversePrimary: 'backgroundInversePrimary',
  backgroundInverseSecondary: 'backgroundInverseSecondary',
  backgroundNegative: 'errorBackgroundBold',
  backgroundNegativeLight: 'errorBackgroundSubtle',
  backgroundOverlayArt: 'backgroundOverlayArt',
  backgroundOverlayLight: 'backgroundOverlayArt',
  backgroundPositive: 'successBackgroundBold',
  backgroundPositiveLight: 'successBackgroundSubtle',
  backgroundPrimary: 'backgroundPrimary',
  backgroundSecondary: 'backgroundSecondary',
  backgroundStateDisabled: 'backgroundDisabled',
  backgroundTertiary: 'backgroundTertiary',
  backgroundWarning: 'warningBackgroundBold',
  backgroundWarningLight: 'warningBackgroundSubtle',
  borderAccessible: 'borderAccessible',
  borderInverseOpaque: 'borderInverseAccessible',
  borderInverseSelected: 'borderInverseBold',
  borderInverseTransparent: 'borderInverseSubtle',
  borderNegative: 'errorBorderAccessible',
  borderNegativeLight: 'errorBorderSubtle',
  borderOpaque: 'borderSubtle',
  borderPositive: 'successBorderAccessible',
  borderPositiveLight: 'successBorderSubtle',
  borderSelected: 'borderBold',
  borderStateDisabled: 'borderSubtle',
  borderTransparent: 'borderSubtle',
  borderWarning: 'warningBorderAccessible',
  borderWarningLight: 'warningBorderSubtle',
  brandBackgroundDisabled: 'accentBackgroundPrimaryDisabled',
  brandBackgroundPrimary: 'accentBackgroundPrimaryBold',
  brandBackgroundSecondary: 'accentBackgroundPrimarySubtle',
  brandBackgroundTertiary: 'accentBackgroundPrimarySubtle',
  brandBorderAccessible: 'accentBorderPrimaryAccessible',
  brandBorderSubtle: 'accentBorderPrimarySubtle',
  brandContentDisabled: 'accentContentPrimaryDisabled',
  brandContentOnGradient: 'accentContentOngradient',
  brandContentOnPrimary: 'accentContentPrimaryOnbold',
  brandContentOnSecondary: 'accentContentPrimaryOnsubtle',
  brandContentOnTertiary: 'accentContentPrimaryOnsubtle',
  brandContentPrimary: 'accentContentPrimary',
  brandGradientStop1: 'accentGradientStop1',
  brandGradientStop2: 'accentGradientStop2',
  brandGradientStop3: 'accentGradientStop3',
  contentInversePrimary: 'contentInversePrimary',
  contentInverseSecondary: 'contentInverseSecondary',
  contentInverseTertiary: 'contentInverseTertiary',
  contentNegative: 'errorContentPrimary',
  contentOnColor: 'contentAlwaysLight',
  contentOnColorInverse: 'contentAlwaysDark',
  contentPositive: 'successContentPrimary',
  contentPrimary: 'contentPrimary',
  contentSecondary: 'contentSecondary',
  contentStateDisabled: 'contentDisabled',
  contentTertiary: 'contentTertiary',
  contentWarning: 'warningContentPrimary',
  gradientStop1: 'accentGradientStop1',
  gradientStop2: 'accentGradientStop2',
  gradientStop3: 'accentGradientStop3',
  hoverNegativeAlpha: 'errorBackgroundStatelayerHover',
  hoverOverlayAlpha: 'backgroundStatelayerHover',
  hoverOverlayInverseAlpha: 'backgroundInverseStatelayerHover',
  pressedNegativeAlpha: 'errorBackgroundStatelayerPressed',
  pressedOverlayAlpha: 'backgroundStatelayerPressed',
  pressedOverlayInverseAlpha: 'backgroundInverseStatelayerPressed'
} as const) satisfies Partial<Record<V1SemanticTokenKey, V2SemanticTokenKey>>);

/** V2 token keys reachable via the primitive mapping. */
export type V2MappedPrimitiveToken = (typeof V1_PRIMITIVE_TO_V2_PRIMITIVE)[keyof typeof V1_PRIMITIVE_TO_V2_PRIMITIVE];

/** V2 token keys reachable via the semantic mapping. */
export type V2MappedSemanticToken = (typeof V1_SEMANTIC_TO_V2_SEMANTIC)[keyof typeof V1_SEMANTIC_TO_V2_SEMANTIC];

/** Any V2 token key returned by the v1 → v2 mappers. */
export type V2MappedColorToken = V2MappedPrimitiveToken | V2MappedSemanticToken;